import React, { useContext, useEffect, useRef, useState } from "react";
import PagePanel from "app/components/PagePanel";
import styles from "./styles.module.scss";
import AddAvailability from "./Availability/Modals/AddAvailability";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import ScheduleDropdown from "app/routes/Scheduling/Availability/ScheduleDropdown";
import AvailabilityWithDates from "app/routes/Scheduling/Availability/AvailabilityWithDates";
import {
  dateToString,
  parseSchedules,
  stringToDate,
} from "app/routes/Scheduling/utils";
import {
  schedulingFetchDelete,
  schedulingFetchGet,
  schedulingFetchPatch,
  schedulingFetchPost,
} from "app/utils/request/scheduling/fetch";
import InfoModal from "app/routes/Calendar/InfoModal";
import useConfirmation from "app/routes/Calendar/hooks/useConfirmation";
import ConfirmationPrompt from "app/routes/Calendar/ConfirmationPrompt";
import { useTranslation } from "react-i18next";
import RemoveAvailability from "./Availability/Modals/RemoveAvailability";
import { DateTime } from "luxon";
import { useConstants } from "./constants";
import { UserContext } from "app/contexts/UserContext";
import { useSelectedCoach } from "app/contexts/SelectedCoachContext";
import { MinimalBookingOption } from "app/routes/Scheduling/Availability/BookingOptionSelector";
import ButtonVariant from "app/components/Button/ButtonVariant";
import TextInput from "app/components/inputs/TextInput";
import CalendarSettingsLayout from "./Layout";
import CoachSelector from "app/components/CoachSelector";
import useUnsavedChangesPrompt from "app/utils/hooks/useUnsavedChangesPrompt";
import UnsavedChangesModal from "./Availability/Modals/UnsavedChanges";
import { useHistory } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import useActions from "app/utils/hooks/useActions";
import { fetchAcademySetup } from "app/actions/academySetup";
import {
  isSetupItemOpen,
  showAvailabilitySetToast,
} from "app/components/AcademySetup/SetupToast";
import SetupHint from "app/components/AcademySetup/SetupHint";
import { isAcademyOnboardingCoach } from "app/utils/subscription";

interface DailyTimeTable {
  day: number;
  active: boolean;
  startTime: string;
  endTime: string;
  value: number;
  label: string;
  short: string;
}

interface TimeTable {
  id?: string;
  name: string;
  dailyTimeTables: DailyTimeTable[];
  customPeriod: boolean;
  startDate: string | Date;
  endDate: string | Date;
  lessonRates?: any[];
}

// Thursday in days_of_the_week (Sunday is 0); the coachmark's anchor (Figma 531:9999).
const THURSDAY = 4;

interface InfoPopupContent {
  title: string;
  status: "success" | "error";
  content: string;
  subcontent?: string;
}

export default function Availability() {
  return (
    <CalendarSettingsLayout>
      <AvailabilityContent />
    </CalendarSettingsLayout>
  );
}

function AvailabilityContent() {
  const { t } = useTranslation();
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isRemoveModalOpen, setRemoveModalOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  // True once the first time_tables fetch has settled, so the empty-state
  // coachmark below waits for the answer instead of flashing while loading.
  const [schedulesLoaded, setSchedulesLoaded] = useState(false);
  const [bookingOptions, setBookingOptions] = useState<MinimalBookingOption[]>(
    []
  );
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savingFromPrompt, setSavingFromPrompt] = useState(false);
  const { isPromptOpen, proceed, stay } =
    useUnsavedChangesPrompt(hasUnsavedChanges);
  const [currentItem, setCurrentItem] = useState(null);
  const user = useContext(UserContext);
  const { selectedCoachId: contextCoachId } = useSelectedCoach();
  // Use context coach ID if available (for admin mode), otherwise fall back to current user
  const selectedCoachId = contextCoachId || user.id;
  const [infoPopupContent, setInfoPopupContent] = useState<InfoPopupContent>({
    title: "",
    status: "error",
    content: "",
  });
  const { days_of_the_week } = useConstants();
  const history = useHistory();
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const academySetupActions = useActions({ fetchAcademySetup });
  const thursdayRef = useRef<HTMLButtonElement>(null);
  // The arrow points at Thu, but choosing any day is taking the hint.
  const daysRowRef = useRef<HTMLDivElement>(null);
  // Fallback anchor for the calendar_options coachmark when the coach has no
  // schedule to point into: the "+ Add Schedule" control in the header.
  const addScheduleRef = useRef<HTMLDivElement>(null);

  /**
   * Whether this save is the one that completes the checklist's
   * calendar_options item. Read before the request: the alert's own gate only
   * asks whether the item is complete now, so without this every later save
   * would re-celebrate. The checklist tracks the logged-in coach's own setup,
   * so an admin editing someone else's schedule through CoachSelector keeps
   * the regular confirmation.
   */
  const completesAcademySetupItem = () =>
    selectedCoachId === user.id &&
    isAcademyOnboardingCoach(self) &&
    isSetupItemOpen("calendar_options");

  /** The designed alert, or false when this save didn't complete the item. */
  const celebrateAvailability = (completes: boolean): Promise<boolean> => {
    if (!completes) {
      return Promise.resolve(false);
    }
    return showAvailabilitySetToast(academySetupActions.fetchAcademySetup, (path, state) =>
history.push(path, state)
    );
  };

  const defaultSchedule = {
    name: "",
    dailyTimeTables: days_of_the_week.map((day) => ({
      ...day,
      label: day.label,
      short: day.short,
      day: day.value,
      active: false,
      startTime: "08:00",
      endTime: "17:00",
    })),
    customPeriod: false,
    startDate: DateTime.now().startOf("year").toJSDate(),
    endDate: DateTime.now().endOf("year").toJSDate(),
  };

  const [newSchedule, setNewSchedule] = useState<TimeTable>(defaultSchedule);

  const openAddModal = () => {
    setNewSchedule({
      ...defaultSchedule,
      lessonRates: bookingOptions,
    });
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setNewSchedule(defaultSchedule);
  };

  const openRemoveModal = (schedule: any) => {
    setRemoveModalOpen(true);
    setCurrentItem(schedule);
  };
  const closeRemoveModal = () => setRemoveModalOpen(false);

  const { PromptComponent } = useConfirmation((props) => (
    <ConfirmationPrompt {...props} />
  ));

  const facility = user.facility;
  const facilityBookingEnabled = facility?.featureFlags?.facilityLevelBookingOptions === true;

  useEffect(() => {
    if (!selectedCoachId) {
      return;
    }

    if (facilityBookingEnabled && facility?.id) {
      // Fetch facility-level booking options
      schedulingFetchGet(`facilities/${facility.id}/lesson_rates`, {})
        .then((lessonRates) => {
          const opts = lessonRates
            .filter((lr: any) => lr.active !== false)
            .map((lr: any) => ({ id: lr.id, title: lr.title }));
          setBookingOptions(opts);
        })
        .catch(() => {
          setInfoPopupContent({
            title: "Oops...",
            status: "error",
            content: t("Something went wrong."),
            subcontent: t("Please try again."),
          });
        });
    } else {
      // Fetch coach-level booking options
      schedulingFetchGet(`coach_profiles/${selectedCoachId}/pricing_groups`, {})
        .then((pricingGroups) => {
          const opts = pricingGroups[0].lessonRates
            .filter((lr: any) => lr.active)
            .map((lr: any) => {
              return { id: lr.id, title: lr.title };
            });
          setBookingOptions(opts);
        })
        .catch(() => {
          setInfoPopupContent({
            title: "Oops...",
            status: "error",
            content: t("Something went wrong."),
            subcontent: t("Please try again."),
          });
        });
    }
  }, [selectedCoachId, facility?.id, facilityBookingEnabled]);

  useEffect(() => {
    if (!selectedCoachId) {
      return undefined;
    }
    // Switching coaches (CoachSelector) starts over: the previous coach's
    // schedules are not "loaded" for the new one, and a slow response for
    // the old coach must not land on the new selection.
    let stale = false;
    setSchedulesLoaded(false);
    schedulingFetchGet<TimeTable[]>(`coach_profiles/${selectedCoachId}/time_tables`, {})
      .then((time_tables) => {
        if (stale) {
          return;
        }
        setSchedules(
          time_tables.map((time_table) =>
            parseSchedules(time_table, days_of_the_week)
          ) || []
        );
        setHasUnsavedChanges(false);
      })
      .catch(() => {
        if (stale) {
          return;
        }
        setInfoPopupContent({
          title: "Oops...",
          status: "error",
          content: t("Something went wrong."),
          subcontent: t("Please try again."),
        });
      })
      .finally(() => {
        if (!stale) {
          setSchedulesLoaded(true);
        }
      });
    return () => {
      stale = true;
    };
  }, [selectedCoachId]);

  const methods = useForm({
    defaultValues: schedules,
    mode: "onChange",
  });

  const { handleSubmit } = methods;

  // Access control is handled centrally in CalendarSettingsLayout
  // which shows loading state while facility loads and redirects if unauthorized

  const deleteSchedule = async (id: number) => {
    return schedulingFetchDelete(`time_tables/${id}`)
      .then(() => {
        setSchedules(schedules.filter((schedule) => schedule.id !== id));
      })
      .catch(() => {
        setInfoModalOpen(true);
        setInfoPopupContent({
          title: "Oops...",
          status: "error",
          content: t("Something went wrong."),
          subcontent: t("Please try again."),
        });
      });
  };

  const handleAddAvailability = (time_table: TimeTable) => {
    time_table.id = null;
    time_table = dateToString(time_table);

    const params = {
      ...time_table,
      lessonRateIds: time_table.lessonRates?.map((lr) => lr.id) || [],
    };

    if (schedules.filter((sch) => sch.name === time_table.name).length > 0) {
      setInfoModalOpen(true);
      setInfoPopupContent({
        title: t("Invalid name"),
        status: "error",
        content: t("The schedule name is already in use."),
        subcontent: t("Please select another name."),
      });
      return null;
    }
    const completes = completesAcademySetupItem();
    return schedulingFetchPost<TimeTable>(`coach_profiles/${selectedCoachId}/time_tables`, { time_table: params })
      .then(async (new_schedule) => {
        time_table.id = new_schedule.id;
        setSchedules([stringToDate(time_table), ...schedules]);
        // The designed alert replaces the generic modal — one confirmation
        // per completion, as on /calendar/pricing. It only fires if the API
        // agrees the item is done, so a schedule the BE doesn't count (no
        // active days, say) still gets the modal.
        if (await celebrateAvailability(completes)) {
          return;
        }
        setInfoModalOpen(true);
        setInfoPopupContent({
          title: t("Success"),
          status: "success",
          content: t("Schedule successfully added."),
        });
      })
      .catch((error) => {
        if (
          error.errors[0]?.source?.parameter === "daily_time_tables.begin_at"
        ) {
          setInfoModalOpen(true);
          setInfoPopupContent({
            title: t("Invalid time"),
            status: "error",
            content: error[0]?.detail,
            subcontent: t("Please select another time."),
          });
        } else if (error.errors[0]?.source?.parameter === "active_in") {
          setInfoModalOpen(true);
          setInfoPopupContent({
            title: t("Overlap times"),
            status: "error",
            content: t(
              "The new availability schedule overlaps with an existing availability schedule."
            ),
            subcontent: t("Please select another time."),
          });
        }
      });
  };

  const handleUpdateAvailability = (
    time_tables: TimeTable[],
    options?: {
      skipSuccessToast?: boolean;
      /** Save-and-leave: runs the blocked navigation as soon as the save lands. */
      onSaved?: () => void;
    }
  ): Promise<boolean> => {
    let params = [...time_tables.filter((sch) => sch.id).map(dateToString)];
    params = params.map((timeTable: TimeTable) => {
      return {
        ...timeTable,
        lessonRateIds: timeTable.lessonRates?.map((lr) => lr.id) || [],
      };
    });

    const completes = completesAcademySetupItem();
    return schedulingFetchPatch<{ data: any[] }>(`coach_profiles/${selectedCoachId}/time_tables/bulk_update`, {
      time_tables: params,
    })
      .then(async (response) => {
        if (options?.onSaved) {
          // Save-and-leave: navigate first, then celebrate, so the rail is
          // shown on the destination page — the sidebar's route watcher
          // would dismiss one shown here the moment the route changed. The
          // page is gone after this, so none of the updates below apply.
          options.onSaved();
          await celebrateAvailability(completes);
          return true;
        }
        const celebrated = await celebrateAvailability(completes);
        if (!celebrated && !options?.skipSuccessToast) {
          setInfoModalOpen(true);
          setInfoPopupContent({
            title: t("Changes saved"),
            status: "success",
            content: t("Availability successfully saved."),
          });
        }
        const scheds = response.data.map((tt) => {
          const toParse = {
            ...tt,
            ...tt.attributes,
            dailyTimeTables: tt.attributes.daily,
          };
          return parseSchedules(toParse, days_of_the_week);
        });
        setSchedules(scheds);
        setHasUnsavedChanges(false);
        return true;
      })
      .catch((error) => {
        if (error === undefined) {
          setInfoModalOpen(true);
          setInfoPopupContent({
            title: t("Oops..."),
            status: "error",
            content: t("Something went wrong."),
            subcontent: t("Please try again."),
          });
          return false;
        }
        if (
          error.errors[0]?.source?.parameter === "daily_time_tables.begin_at"
        ) {
          setInfoModalOpen(true);
          setInfoPopupContent({
            title: t("Invalid time"),
            status: "error",
            content: error[0]?.detail,
            subcontent: t("Please select another time."),
          });
        } else if (error.errors[0]?.source?.parameter === "active_in") {
          setInfoModalOpen(true);
          setInfoPopupContent({
            title: t("Overlap times"),
            status: "error",
            content: t(
              "The new availability schedule overlaps with an existing availability schedule."
            ),
            subcontent: t("Please select another time."),
          });
        }
        return false;
      });
  };

  const handleDeleteAvailability = (schedule: any) => {
    deleteSchedule(schedule.id);
    closeRemoveModal();
  };

  const onSubmit: SubmitHandler<TimeTable[]> = () => {
    handleUpdateAvailability(schedules);
  };

  const setScheduleData = (id: string, key: string, value: unknown) => {
    const updatedSchedules = schedules.map((schedule) => {
      if (schedule.id === id) {
        return {
          ...schedule,
          [key]: value,
        };
      }
      return schedule;
    });

    setSchedules(updatedSchedules);
    setHasUnsavedChanges(true);
  };

  const confirmButton = (
    <ButtonVariant
      title={t("Save changes")}
      buttonType="primary"
      onClickFunc={handleSubmit(onSubmit)}
      buttonStyles={styles.confirm_button}
      childStyles={styles.button_text}
      data-testid="save-availability-button"
    />
  );

  return (
    <PagePanel
      title={t("Availability")}
      titleStyles={styles.page_panel_title}
      headerChildren={
        <div className={styles.page_panel} ref={addScheduleRef}>
          {schedulesLoaded && schedules.length === 0 && (
            <SetupHint
              itemKey="calendar_options"
              anchorRef={addScheduleRef}
              placement="bottom-end"
              arrowAtAnchorCenter
            />
          )}
          <ButtonVariant
            title={t("+ Add Schedule")}
            buttonType='primary-outline'
            onClickFunc={openAddModal}
            data-testid="add-schedule-button"
          />
        </div>
      }
    >
      <CoachSelector />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {schedules.map((schedule, index) => (
            <ScheduleDropdown
              key={schedule.id}
              schedule={schedule}
              noSchedules={schedules.length}
              deleteSchedule={() => openRemoveModal(schedule)}
              index={index}
            >
              <div className={styles.elements}>
                <div className={styles.form_row}>
                  <TextInput
                    label={t("Schedule Name")}
                    value={schedule.name}
                    onChange={(e) => setScheduleData(schedule.id, 'name', e.target.value)}
                    name="schedule"
                    required
                    data-testid="schedule-name-input"
                  />
                </div>
                {/* booking option here */}
                <AvailabilityWithDates
                  schedule={schedule}
                  setScheduleData={(key, value) => setScheduleData(schedule.id, key, value)}
                  bookingOptions={bookingOptions}
                  anchorDay={index === 0 ? THURSDAY : undefined}
                  anchorRef={index === 0 ? thursdayRef : undefined}
                  daysRef={index === 0 ? daysRowRef : undefined}
                />
                {index === 0 && (
                  <SetupHint
                    itemKey="calendar_options"
                    anchorRef={thursdayRef}
                    dismissRef={daysRowRef}
                    placement="bottom-end"
                    arrowAtAnchorCenter
                  />
                )}
              </div>
            </ScheduleDropdown>
          ))}
          <div className={styles.buttons_container}>{confirmButton}</div>
        </form>
      </FormProvider>
      <AddAvailability
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        onAddAvailability={handleAddAvailability}
        schedule={newSchedule}
        setSchedule={setNewSchedule}
        bookingOptions={bookingOptions}
      />
      <RemoveAvailability
        isOpen={isRemoveModalOpen}
        onClose={closeRemoveModal}
        onDelete={() => handleDeleteAvailability(currentItem)}
        title={currentItem?.name}
      />
      <InfoModal
        isOpen={isInfoModalOpen}
        title={infoPopupContent.title}
        status={infoPopupContent.status}
        onDismiss={() => setInfoModalOpen(false)}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div>{infoPopupContent.content}</div>
          <div>
            <b> {infoPopupContent.subcontent}</b>
          </div>
        </div>
      </InfoModal>
      <UnsavedChangesModal
        isOpen={isPromptOpen}
        saving={savingFromPrompt}
        onCancel={stay}
        onDiscard={proceed}
        onSave={async () => {
          setSavingFromPrompt(true);
          const ok = await handleUpdateAvailability(schedules, {
            skipSuccessToast: true,
            onSaved: proceed,
          });
          if (!ok) {
            // On success the navigation has already left this page.
            setSavingFromPrompt(false);
          }
        }}
      />
      <PromptComponent />
    </PagePanel>
  );
}
